import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { invitationService, type CreateInvitationDto } from "@/services/invitation/invitation.service";
import { UtilsService } from "@/services/utils/utils.service";
import { useCompanyStore } from "@/store/use-company-store";
import { useAuthStore } from "@/store/use-auth-store";

const utils = new UtilsService();

export const useInvitations = () => {
  const { currentCompanyId } = useCompanyStore();
  const { isAuthenticated } = useAuthStore();
  
  return useQuery({
    queryKey: ["invitations", currentCompanyId],
    queryFn: () => invitationService.getInvitations(),
    enabled: isAuthenticated && !!currentCompanyId,
    retry: false,
  });
};

export const useCreateInvitations = () => {
  const queryClient = useQueryClient();
  const { currentCompanyId } = useCompanyStore();

  return useMutation({
    mutationFn: (data: CreateInvitationDto) => invitationService.createInvitations(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invitations", currentCompanyId] });
    },
    onError: (error: any) => {
      // Error handling is done in the component
      throw error;
    },
  });
};

export const useValidateInvitation = () => {
  return useMutation({
    mutationFn: (token: string) => invitationService.validateInvitation(token),
  });
};

export const useAcceptInvitation = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (token: string) => invitationService.acceptInvitation(token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company"] });
      queryClient.invalidateQueries({ queryKey: ["onboardingStatus"] });
      toast.success(t("onboarding.invitationAccepted"));
    },
    onError: (error: unknown) => {
      const errorMessage = utils.extractErrorMessage(
        error,
        t("onboarding.invitationAcceptFailed")
      );
      toast.error(errorMessage);
    },
  });
};

